import { cn } from '@/lib/utils';
import { ScorecardRichText } from '@/components/scorecard/ScorecardRichText';

export type EngagementMetricStructured = {
  id?: string;
  title?: string;
  pillar?: string;
  score_pct?: number | null;
  overall_feedback?: string;
  detailed_feedback?: string;
};

export type EngagementStructured = {
  engagement_pct?: number | null;
  story_spine_pct?: number | null;
  character_strength_pct?: number | null;
  theme_strength_pct?: number | null;
  metrics?: EngagementMetricStructured[];
};

type EngagementSectionProps = {
  markdown: string;
  structured?: EngagementStructured | null;
};

const PILLARS: { id: string; title: string }[] = [
  { id: 'story', title: 'Story' },
  { id: 'character', title: 'Character' },
  { id: 'theme', title: 'Theme' },
];

/** Map metric id → pillar when older payloads omit pillar. */
const METRIC_PILLAR_FALLBACK: Record<string, string> = {
  etb_structure_pct: 'story',
  story_density_pct: 'story',
  pacing_pct: 'story',
  scene_strength_pct: 'story',
  character_concentration_pct: 'character',
  character_resonance_pct: 'character',
  character_active_pct: 'character',
  dialogue_strength_pct: 'character',
  character_journey_pct: 'character',
  thematic_quality_pct: 'theme',
  authenticity_pct: 'theme',
  genre_promise_pct: 'theme',
};

/** Metrics removed from Engagement (A.1–A.3) — never render even if present in old payloads. */
const EXCLUDED_METRIC_IDS = new Set([
  'story_beat_strength_pct',
  'concentration_pct',
]);

function pct(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '—';
  return `${Math.round(v)}%`;
}

function resolvePillar(metric: EngagementMetricStructured): string {
  if (metric.pillar === 'story' || metric.pillar === 'character' || metric.pillar === 'theme') {
    return metric.pillar;
  }
  // Legacy pillar name
  if (metric.pillar === 'story_spine') return 'story';
  if (metric.id && METRIC_PILLAR_FALLBACK[metric.id]) {
    return METRIC_PILLAR_FALLBACK[metric.id];
  }
  return '';
}

function MetricCard({ metric }: { metric: EngagementMetricStructured }) {
  const title = metric.title ?? 'Metric';
  return (
    <div className="mb-8 pb-6 border-b border-[#2a2a2a] last:border-0">
      <div className="flex flex-wrap items-baseline gap-3 mb-4">
        <h4 className="text-base font-semibold text-white">{title}</h4>
        <span className="text-sm text-white/60">Score: {pct(metric.score_pct)}</span>
      </div>
      {metric.overall_feedback?.trim() ? (
        <div className="mb-4">
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/45 mb-2">
            Overall User Feedback
          </p>
          <ScorecardRichText text={metric.overall_feedback} />
        </div>
      ) : null}
      {metric.detailed_feedback?.trim() ? (
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/45 mb-2">
            Detailed User Feedback
          </p>
          <ScorecardRichText text={metric.detailed_feedback} />
        </div>
      ) : null}
    </div>
  );
}

export function EngagementSection({ markdown, structured }: EngagementSectionProps) {
  const metrics =
    structured?.metrics?.filter(
      (m) => m?.title && !(m.id && EXCLUDED_METRIC_IDS.has(m.id)),
    ) ?? [];

  if (metrics.length > 0) {
    return (
      <div className="space-y-2">
        <div className="flex flex-wrap gap-4 mb-6 text-sm text-white/70">
          <span>
            Overall:{' '}
            <span className={cn('font-semibold text-white')}>{pct(structured?.engagement_pct)}</span>
          </span>
          <span>Story: {pct(structured?.story_spine_pct)}</span>
          <span>Character: {pct(structured?.character_strength_pct)}</span>
          <span>Theme: {pct(structured?.theme_strength_pct)}</span>
        </div>
        {PILLARS.map((pillar) => {
          const pillarMetrics = metrics.filter((m) => resolvePillar(m) === pillar.id);
          if (pillarMetrics.length === 0) return null;
          return (
            <div key={pillar.id} className="mb-10">
              <h3 className="text-lg font-semibold text-white mb-4 pb-2 border-b border-[#3a3a3a]">
                {pillar.title}
              </h3>
              {pillarMetrics.map((m, i) => (
                <MetricCard key={m.id ?? m.title ?? i} metric={m} />
              ))}
            </div>
          );
        })}
      </div>
    );
  }

  return null;
}

export default EngagementSection;
